import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Alert, 
  CircularProgress, 
  Grid,
  Paper,
  Chip,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import { DragDropContext } from 'react-beautiful-dnd';
import {
  FiberNew as NewIcon,
  CheckCircle as QualifiedIcon,
  Cancel as DisqualifiedIcon,
  TrendingUp as WonIcon,
  TrendingDown as LossIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import Column from './Column';
import SearchAndFilters from '../SearchAndFilters/SearchAndFilters';
import { updateLead } from '../../../../Action/api';

const Board = ({ 
  leads,
  loading, 
  error, 
  searchTerm, 
  setSearchTerm, 
  filterType, 
  setFilterType, 
  onRefresh, 
  onViewDetails,
  onUpdateLead,
  setSnackbar,
  month // <-- Receive month prop
}) => {
  // const [leads, setLeads] = useState(initialLeads || []);
  // const [leads, setLeads] = useState(initialLeads || []);
  const [updating, setUpdating] = useState(false);
  const theme = useTheme();

  const [localLeads, setLocalLeads] = useState(leads || []);
  useEffect(() => {
    setLocalLeads(leads || []);
  }, [leads]);

  const leadColumns = [
    { 
      id: 'New', 
      title: 'New Leads', 
      icon: <NewIcon />,
      color: '#4F46E5', // Indigo
      bgColor: '#F0F4FF',
      borderColor: '#C7D2FE',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    { 
      id: 'Qualified', 
      title: 'Qualified', 
      icon: <QualifiedIcon />,
      color: '#059669', // Emerald
      bgColor: '#ECFDF5',
      borderColor: '#A7F3D0',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    },
    { 
      id: 'Disqualified', 
      title: 'Disqualified', 
      icon: <DisqualifiedIcon />,
      color: '#DC2626', // Red
      bgColor: '#FEF2F2',
      borderColor: '#FCA5A5',
      gradient: 'linear-gradient(135deg, #ff416c 0%, #ff4757 100%)'
    },
    { 
      id: 'Closed - Won', 
      title: 'Closed Won', 
      icon: <WonIcon />,
      color: '#16A34A', // Green
      bgColor: '#F0FDF4',
      borderColor: '#BBF7D0',
      gradient: 'linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)'
    },
    { 
      id: 'Closed - Loss', 
      title: 'Closed Loss', 
      icon: <LossIcon />,
      color: '#B91C1C', // Dark Red
      bgColor: '#FEF2F2',
      borderColor: '#FECACA',
      gradient: 'linear-gradient(135deg, #c94b4b 0%, #4b134f 100%)'
    }
  ];

  // Filter leads by search term and filter type
  const getFilteredLeads = () => {
    let filtered = localLeads;

    if (searchTerm) {
      filtered = filtered.filter(lead => {
        const name = (lead.fullName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim()).toLowerCase();
        const email = (lead.email || '').toLowerCase();
        const company = (lead.numberOfEmployees || lead.location || '').toLowerCase();
        
        return name.includes(searchTerm.toLowerCase()) ||
               email.includes(searchTerm.toLowerCase()) ||
               company.includes(searchTerm.toLowerCase());
      });
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(lead => {
        if (filterType === 'starter') return lead.planType === 'starter';
        if (filterType === 'professional') return lead.planType === 'professional';
        if (filterType === 'enterprise') return lead.planType === 'enterprise';
        return true;
      });
    }

    return filtered;
  };

  // Group leads by status
  const getLeadsByColumn = () => {
    const filteredLeads = getFilteredLeads();
    const groupedLeads = {};
    
    leadColumns.forEach(column => {
      groupedLeads[column.id] = filteredLeads.filter(lead => lead.leadType === column.id);
    });
    
    return groupedLeads;
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }

    const leadId = draggableId;
    const newStatus = destination.droppableId;
    const lead = localLeads.find(l => l.leadId === leadId);

    if (!lead || lead.leadType === newStatus) return;

    try {
      setUpdating(true);

      const updatedLeadData = {
        ...lead,
        currentLeadType: lead.leadType, // <-- Add this line
        leadType: newStatus,
        updatedAt: new Date().toISOString(),
      };

      const response = await updateLead(updatedLeadData);

      if (response.status) {
        onUpdateLead(leadId, newStatus);
        setSnackbar({
          open: true,
          message: `Lead successfully moved to ${newStatus}`,
          severity: 'success'
        });
      } else {
        throw new Error(response.message || 'Failed to update lead');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      setSnackbar({
        open: true,
        message: `Failed to update lead: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleColumnRefresh = () => {
    if (onRefresh) onRefresh();
  };

  const handleColumnLeadsUpdate = (columnId, newLeads) => {
    setLocalLeads(prevLeads => {
      // Remove old leads for this column
      const filtered = prevLeads.filter(lead => lead.leadType !== columnId);
      // Add new leads with correct leadType
      const updated = [
        ...filtered,
        ...newLeads.map(lead => ({ ...lead, leadType: columnId }))
      ];
      return updated;
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3
      }}>
        <Box sx={{ textAlign: 'center', color: 'white' }}>
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Loading your pipeline...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            borderRadius: 3,
            '& .MuiAlert-icon': { fontSize: 28 }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Something went wrong
          </Typography>
          {error}
        </Alert>
      </Container>
    );
  }

  const leadsByColumn = {};
  leadColumns.forEach(col => {
    leadsByColumn[col.id] = localLeads.filter(lead => lead.leadType === col.id);
  });

  const totalLeads = localLeads.length;
  const filteredCount = getFilteredLeads().length;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(135deg, 
        ${alpha('#f8fafc', 0.9)} 0%, 
        ${alpha('#e2e8f0', 0.8)} 50%, 
        ${alpha('#f1f5f9', 0.9)} 100%)`,
      position: 'relative',
      pb: 4, // Add bottom padding for better spacing
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 30% 20%, rgba(120, 119, 198, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none'
      }
    }}>
      <Container maxWidth="xl" sx={{ py: 3, position: 'relative', zIndex: 1 }}>
        
        {/* Premium Header */}
        <Paper sx={{ 
          mb: 3, 
          p: 3, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          border: '1px solid rgba(255,255,255,0.2)',
          backdropFilter: 'blur(10px)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{ 
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
                }}>
                  <TimelineIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 800, 
                  background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.5px'
                }}>
                  Orucore Pipeline
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon sx={{ color: '#64748b', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#64748b' }}>
                    {totalLeads} Total Leads
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6' }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#64748b' }}>
                    {filteredCount} Filtered
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Pipeline Stats */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {leadColumns.map((column, index) => (
                <Chip
                  key={column.id}
                  icon={column.icon}
                  label={`${leadsByColumn[column.id]?.length || 0}`}
                  sx={{
                    background: column.gradient,
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    height: 40,
                    px: 2,
                    '& .MuiChip-icon': { color: 'white' },
                    boxShadow: `0 4px 12px ${alpha(column.color, 0.3)}`,
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Search and Filters */}
        <Box sx={{ mb: 3 }}>
          <SearchAndFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterType={filterType}
            setFilterType={setFilterType}
            onRefresh={onRefresh}
            updating={updating}
          />
        </Box>

        {/* Board with Natural Height - No height constraints */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Grid container spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'nowrap', overflowX: 'auto' }}>
            {leadColumns.map((column, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                lg={2.4} 
                xl={2.4} 
                key={column.id}
                sx={{
                  minWidth: 340, // Ensures each column is wide enough for drag target
                  maxWidth: 360,
                  flex: '0 0 340px', // Prevents shrinking
                  transition: 'min-width 0.2s'
                }}
              >
                <Fade in timeout={300 + index * 100}>
                  <Box>
                    <Column
                      column={column}
                      leads={leadsByColumn[column.id]}
                      onViewDetails={onViewDetails}
                      updating={updating}
                      onRefresh={handleColumnRefresh}
                      month={month}
                      onColumnLeadsUpdate={handleColumnLeadsUpdate}
                    />
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </DragDropContext>
      </Container>
    </Box>
  );
};

export default Board;